import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { fetchAdminMe, fetchAdminStats, fetchAdminOtpStats } from "@/lib/api";
import AdminHealthPanel from "@/components/AdminHealthPanel";
import AdminProRevenue from "@/components/AdminProRevenue";
import {
  ShieldCheck, Loader2, FileText, Star, Users, MessageCircle, Bell,
  TrendingUp, AlertCircle, ArrowRight, Hotel, ShieldAlert, Phone, PiggyBank, Megaphone, Wifi, CreditCard, Wallet,
} from "lucide-react";
import Sparkline from "@/components/Sparkline";
import { SalesSurveysSection } from "@/components/SalesSurveysSection";
import { withWatermark } from "@/lib/utils";

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [stats, setStats] = useState(null);
  const [otpStats, setOtpStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAdminMe()
      .then(() => setAuthChecked(true))
      .catch(() => navigate("/", { replace: true }));
  }, [navigate]);

  useEffect(() => {
    if (!authChecked) return;
    Promise.all([fetchAdminStats(), fetchAdminOtpStats().catch(() => null)])
      .then(([s, o]) => { setStats(s); setOtpStats(o); })
      .finally(() => setLoading(false));
  }, [authChecked]);

  if (!authChecked || loading) {
    return (
      <main className="max-w-7xl mx-auto px-6 py-20 text-center">
        <Loader2 className="animate-spin mx-auto text-orange-500" />
        <p className="mt-3 text-[var(--ink-soft)]">Chargement du tableau de bord...</p>
      </main>
    );
  }

  const t = stats?.totals || {};
  const g = stats?.growth_7d || {};

  return (
    <main className="max-w-7xl mx-auto px-6 py-10" data-testid="admin-dashboard-page">
      <div>
        <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight inline-flex items-center gap-3">
          <ShieldCheck size={32} className="text-orange-500" /> Tableau de bord
        </h1>
        <p className="text-[var(--ink-soft)] mt-2">Vue d&apos;ensemble de la plateforme Fodindin.</p>
      </div>

      {/* Stat cards */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8" data-testid="admin-stat-cards">
        <StatCard
          icon={FileText}
          label="Annonces"
          value={t.annonces}
          accent="orange"
          sub={g.annonces > 0 ? `+${g.annonces} cette semaine` : "Aucune cette semaine"}
          testid="stat-annonces"
        />
        <StatCard
          icon={Star}
          label="Premium"
          value={t.premium}
          accent="amber"
          sub={`${pct(t.premium, t.annonces)}% des annonces`}
          testid="stat-premium"
        />
        <StatCard
          icon={ShieldCheck}
          label="Verifiees"
          value={t.verified}
          accent="green"
          sub={`${t.pending} en attente`}
          testid="stat-verified"
        />
        <StatCard
          icon={Users}
          label="Utilisateurs"
          value={t.users}
          accent="blue"
          sub={g.users > 0 ? `+${g.users} cette semaine` : "Aucun cette semaine"}
          testid="stat-users"
        />
        <StatCard
          icon={MessageCircle}
          label="Conversations"
          value={t.conversations}
          accent="purple"
          testid="stat-conversations"
        />
        <StatCard
          icon={AlertCircle}
          label="Msg non lus"
          value={t.unread_messages}
          accent="red"
          sub="cumul plateforme"
          testid="stat-unread"
        />
        <StatCard
          icon={Bell}
          label="Push abonnes"
          value={t.push_subscriptions}
          accent="indigo"
          sub="navigateurs"
          testid="stat-push"
        />
        <StatCard
          icon={TrendingUp}
          label="Croissance 7j"
          value={g.annonces}
          accent="emerald"
          sub="nouvelles annonces"
          testid="stat-growth"
        />
        <StatCard
          icon={Wifi}
          label="En ligne maintenant"
          value={t.online_users ?? 0}
          accent="green"
          sub="utilisateurs actifs (2 min)"
          testid="stat-online-users"
        />
      </section>

      {/* Quick actions */}
      <section className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <QuickLink to="/admin/annonces" icon={FileText} title="Gestion annonces" desc="Modifier, supprimer, premium en lot" testid="quick-annonces" />
        <QuickLink to="/admin/hotels" icon={Hotel} title="Gestion hotels" desc="Hotels & disponibilites" testid="quick-hotels" />
        <QuickLink to="/admin/securite" icon={ShieldAlert} title="Sécurité" desc="Journal anti-fraude & verrouillages" testid="quick-security" />
        <QuickLink to="/admin/investissements" icon={PiggyBank} title="Investissements" desc="Bilan mensuel des credits du projet" testid="quick-investments" />
        <QuickLink to="/admin/campagnes" icon={Megaphone} title="Campagnes" desc="Relances Pro & equipements" testid="quick-campagnes" />
        <QuickLink to="/admin/guide-feedback" icon={Star} title="Feedback guide" desc="Avis sur le guide biometrique" testid="quick-guide-feedback" />
        <QuickLink to="/admin/financing" icon={CreditCard} title="Financements" desc="Demandes de credit acheteurs" testid="quick-financing" />
        <QuickLink to="/admin/paiements" icon={Wallet} title="Paiements CinetPay" desc="Statut live & transactions Pro" testid="quick-payments" />
        <QuickLink to="/admin/escrows" icon={ShieldAlert} title="Paiements sequestres" desc="Versements vendeurs, litiges, remboursements" testid="quick-escrows" />
        <QuickLink to="/admin/sms" icon={Phone} title="SMS Africa's Talking" desc="Diagnostic OTP, solde & journal d'envois" testid="quick-sms" />
        <QuickLink to="/messages" icon={MessageCircle} title="Mes messages" desc="Boite de reception" testid="quick-messages" />
      </section>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Plateforme — santé en temps réel */}
        <AdminHealthPanel />

        {/* Fodindin Pro — Revenus en direct */}
        <AdminProRevenue />

        {/* Time series - annonces & users over 30 days */}
        <section className="glass-card p-5 lg:col-span-3" data-testid="daily-chart">
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
            <h2 className="font-bold text-lg">Activite sur les 30 derniers jours</h2>
            <div className="flex items-center gap-3 text-xs">
              <span className="inline-flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-orange-500" /> Annonces
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-sky-500" /> Utilisateurs
              </span>
            </div>
          </div>
          {(stats?.daily_30d || []).length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-orange-600 mb-1">Nouvelles annonces</div>
                <Sparkline data={stats.daily_30d} metric="annonces" color="#f97316" testid="spark-annonces" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-sky-600 mb-1">Nouveaux utilisateurs</div>
                <Sparkline data={stats.daily_30d} metric="users" color="#0ea5e9" testid="spark-users" />
              </div>
            </div>
          ) : (
            <div className="text-sm text-[var(--ink-soft)] py-6 text-center">Pas encore de donnees.</div>
          )}
        </section>

        {/* Recent annonces */}
        <section className="glass-card p-5 lg:col-span-2" data-testid="recent-annonces">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-bold text-lg">Dernieres annonces</h2>
            <Link to="/admin/annonces" className="text-sm text-orange-600 font-semibold inline-flex items-center gap-1">
              Tout voir <ArrowRight size={12} />
            </Link>
          </div>
          {(stats?.recent_annonces || []).length === 0 ? (
            <div className="text-sm text-[var(--ink-soft)] py-6 text-center">Aucune annonce.</div>
          ) : (
            <div className="space-y-3">
              {stats.recent_annonces.map((a) => (
                <Link
                  key={a.id}
                  to="/admin/annonces"
                  className="flex items-center gap-3 p-2 rounded-xl hover:bg-orange-50/60 transition-colors"
                  data-testid={`recent-annonce-${a.id}`}
                >
                  <img
                    src={withWatermark(a.image) || "/placeholder.png"}
                    alt=""
                    className="w-12 h-12 rounded-xl object-cover bg-orange-50 shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate text-sm">{a.title}</div>
                    <div className="text-xs text-[var(--ink-soft)] truncate">
                      {a.category} - {a.city} - {(a.price || 0).toLocaleString("fr-FR")} FCFA
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {a.premium && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 font-bold">PREMIUM</span>}
                    {a.verified && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-bold">OK</span>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Recent users */}
        <section className="glass-card p-5" data-testid="recent-users">
          <h2 className="font-bold text-lg mb-4">Derniers utilisateurs</h2>
          {(stats?.recent_users || []).length === 0 ? (
            <div className="text-sm text-[var(--ink-soft)] py-6 text-center">Aucun utilisateur.</div>
          ) : (
            <div className="space-y-3">
              {stats.recent_users.map((u) => (
                <div key={u.user_id} className="flex items-center gap-3" data-testid={`recent-user-${u.user_id}`}>
                  {u.picture ? (
                    <img src={u.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-orange-100 text-orange-700 font-bold grid place-items-center text-xs">
                      {(u.name || u.email || "?").slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">{u.name || "Sans nom"}</div>
                    <div className="text-xs text-[var(--ink-soft)] truncate">{u.email}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Category breakdown */}
        <section className="glass-card p-5 lg:col-span-3" data-testid="by-category">
          <h2 className="font-bold text-lg mb-4">Repartition par categorie</h2>
          {(stats?.by_category || []).length === 0 ? (
            <div className="text-sm text-[var(--ink-soft)] py-6 text-center">Aucune donnee.</div>
          ) : (
            <div className="space-y-2">
              {stats.by_category.map((c) => {
                const maxCount = Math.max(...stats.by_category.map((x) => x.count));
                const pctW = maxCount > 0 ? Math.round((c.count / maxCount) * 100) : 0;
                return (
                  <div key={c.category} className="flex items-center gap-3" data-testid={`cat-${c.category}`}>
                    <div className="w-32 text-sm font-semibold truncate">{c.category}</div>
                    <div className="flex-1 h-3 bg-orange-50 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-orange-500 to-orange-400 rounded-full transition-all"
                        style={{ width: `${pctW}%` }}
                      />
                    </div>
                    <div className="w-12 text-right text-sm font-bold text-orange-600">{c.count}</div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
        {/* SMS OTP / Anti-fraude */}
        <section className="glass-card p-5 lg:col-span-3" data-testid="otp-stats-section">
          <h2 className="font-bold text-lg mb-1 inline-flex items-center gap-2">
            <Phone size={18} className="text-emerald-600" /> Vérification SMS &amp; anti-fraude
          </h2>
          <p className="text-xs text-[var(--ink-soft)] mb-4">
            Africa&apos;s Talking — mode strict actif en Côte d&apos;Ivoire (chaque dépôt d&apos;annonce CI exige un numéro vérifié par SMS).
          </p>
          {!otpStats ? (
            <div className="text-sm text-[var(--ink-soft)] py-4 text-center">Statistiques OTP indisponibles.</div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <StatCard
                  icon={ShieldCheck}
                  label="Utilisateurs vérifiés"
                  value={otpStats.global.verified_users}
                  accent="emerald"
                  sub={`${otpStats.global.global_verification_rate_pct}% des déposants`}
                  testid="otp-stat-verified-users"
                />
                <StatCard
                  icon={Phone}
                  label="SMS OTP envoyés (7j)"
                  value={otpStats.global.otps_sent_7d}
                  accent="blue"
                  sub={`${otpStats.global.pending_otps} en attente`}
                  testid="otp-stat-sms-7d"
                />
                <StatCard
                  icon={ShieldAlert}
                  label="Annonces non vérifiées"
                  value={otpStats.global.unverified_ads_total}
                  accent="red"
                  sub={`+${otpStats.global.unverified_ads_7d} cette semaine`}
                  testid="otp-stat-unverified-ads"
                />
                <StatCard
                  icon={Users}
                  label="Déposants actifs"
                  value={otpStats.global.users_with_ad}
                  accent="indigo"
                  sub="utilisateurs ayant publié"
                  testid="otp-stat-depositors"
                />
              </div>

              <h3 className="font-semibold text-sm mb-2 text-[var(--ink)]">Taux de vérification par pays</h3>
              <div className="space-y-2" data-testid="otp-per-country-list">
                {otpStats.per_country.map((row) => (
                  <div key={row.country} className="flex items-center gap-3" data-testid={`otp-country-${row.country}`}>
                    <div className="w-10 text-sm font-bold text-orange-700">{row.country}</div>
                    <div className="flex-1 h-3 bg-emerald-50 rounded-full overflow-hidden relative">
                      <div
                        className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all"
                        style={{ width: `${row.verification_rate_pct}%` }}
                      />
                    </div>
                    <div className="w-16 text-right text-sm font-bold text-emerald-700">
                      {row.verification_rate_pct}%
                    </div>
                    <div className="w-28 text-right text-xs text-[var(--ink-soft)] hidden sm:block">
                      {row.verified_depositors}/{row.depositors} déposants
                    </div>
                    {row.blocked_annonces > 0 && (
                      <div className="text-xs font-semibold text-red-600 inline-flex items-center gap-1">
                        <ShieldAlert size={11} /> {row.blocked_annonces}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </section>

        <SalesSurveysSection />
      </div>
    </main>
  );
}

function pct(n, d) {
  if (!d) return 0;
  return Math.round((n / d) * 100);
}

const ACCENTS = {
  orange: "from-orange-500 to-orange-400 text-orange-600 bg-orange-50",
  amber: "from-amber-500 to-yellow-400 text-amber-600 bg-amber-50",
  green: "from-emerald-500 to-green-400 text-emerald-600 bg-emerald-50",
  emerald: "from-emerald-500 to-teal-400 text-emerald-600 bg-emerald-50",
  blue: "from-sky-500 to-blue-400 text-sky-600 bg-sky-50",
  purple: "from-violet-500 to-purple-400 text-violet-600 bg-violet-50",
  red: "from-red-500 to-rose-400 text-red-600 bg-red-50",
  indigo: "from-indigo-500 to-blue-400 text-indigo-600 bg-indigo-50",
};

function StatCard({ icon: Icon, label, value, sub, accent = "orange", testid }) {
  const cls = ACCENTS[accent] || ACCENTS.orange;
  return (
    <div className="glass-card p-4 relative overflow-hidden" data-testid={testid}>
      <div className={`absolute -right-4 -top-4 w-20 h-20 rounded-full ${cls.split(" ").slice(-1)} opacity-50`} aria-hidden />
      <div className={`relative inline-flex items-center gap-2 ${cls.split(" ").filter((c) => c.startsWith("text-")).join(" ")}`}>
        <Icon size={16} />
        <span className="text-[10px] uppercase tracking-widest font-bold">{label}</span>
      </div>
      <div className="text-3xl font-extrabold mt-2 relative">{(value ?? 0).toLocaleString("fr-FR")}</div>
      {sub && <div className="text-xs text-[var(--ink-soft)] mt-0.5 relative">{sub}</div>}
    </div>
  );
}

function QuickLink({ to, icon: Icon, title, desc, testid }) {
  return (
    <Link
      to={to}
      className="glass-card p-4 flex items-center gap-3 hover:translate-y-[-1px] transition-all group"
      data-testid={testid}
    >
      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-500 to-orange-400 text-white grid place-items-center shrink-0">
        <Icon size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold">{title}</div>
        <div className="text-xs text-[var(--ink-soft)] truncate">{desc}</div>
      </div>
      <ArrowRight size={16} className="text-[var(--ink-soft)] group-hover:text-orange-500 transition-colors" />
    </Link>
  );
}
